// v2.18.1 — Pinger GPS toutes les 30s pendant qu'une course est active.
// - getCurrentPosition à chaque tick (high accuracy)
// - POST /api/v3/courier/courses/:id/ping avec coords + speed + accuracy
// - Gère erreurs géoloc sans interrompre la course (log warning + continue)
// - Cleanup propre au unmount ou changement d'id

import { useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const PING_INTERVAL_MS = 30000;

export default function useCoursePinger(course_id, { enabled = true, onPing = null } = {}) {
  const timerRef = useRef(null);
  const inflightRef = useRef(false);

  useEffect(() => {
    if (!enabled || !course_id) return undefined;

    const tick = async () => {
      if (inflightRef.current) return;
      if (!navigator.geolocation) {
        // eslint-disable-next-line no-console
        console.warn("[useCoursePinger] geolocation API unavailable");
        return;
      }
      inflightRef.current = true;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude, speed, accuracy } = pos.coords || {};
          const payload = {
            lat: latitude,
            lng: longitude,
            speed_kmh: Number.isFinite(speed) ? +(speed * 3.6).toFixed(2) : null,
            accuracy_m: Number.isFinite(accuracy) ? Math.round(accuracy) : null,
          };
          // v2.18.5 — Émet un waypoint local pour le watchdog anti-embouteillage.
          try {
            window.dispatchEvent(new CustomEvent("civ-courier-waypoint", {
              detail: { lat: latitude, lng: longitude, ts: Date.now(), speed_kmh: payload.speed_kmh },
            }));
          } catch (_) { /* ignore */ }
          try {
            const r = await axios.post(
              `${API}/v3/courier/courses/${course_id}/ping`,
              payload,
              { withCredentials: true },
            );
            if (typeof onPing === "function" && r.data) onPing(r.data);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[useCoursePinger] ping failed:", e.response?.data?.error || e.message);
          } finally {
            inflightRef.current = false;
          }
        },
        (err) => {
          inflightRef.current = false;
          // eslint-disable-next-line no-console
          console.warn("[useCoursePinger] geoloc error:", err.code, err.message);
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 5000 },
      );
    };

    // Premier tick après 5s (laisse le temps à la géoloc de stabiliser).
    const initial = setTimeout(tick, 5000);
    timerRef.current = setInterval(tick, PING_INTERVAL_MS);

    return () => {
      clearTimeout(initial);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [course_id, enabled, onPing]);
}
